"use client";

import React, {createContext, useContext, useEffect, useMemo, useState} from "react";

/**
 * What the open workflow builder offers the GailGPT dock beside it. The dock
 * chat edits the saved Omega draft directly, so the canvas must hand over its
 * pending edits before a turn and take the saved draft back afterwards.
 */
export type WorkflowEditorBinding = {
   workflowId: string;
   /** Saves pending canvas edits. Resolves false when the save failed and the turn must not start. */
   prepareTurn: () => Promise<boolean>;
   /**
    * Locks the canvas, autosave, and the builder's own Publish while the dock
    * changes the saved workflow, so the two cannot diverge. Holds nest; call
    * the returned function to release this one.
    */
   holdCanvas: () => () => void;
   /** The saved workflow changed outside the canvas. Resolves once the builder shows it. */
   onDraftSaved: () => Promise<void>;
};

type WorkflowEditorBridgeValue = {
   binding: WorkflowEditorBinding | null;
   setBinding: React.Dispatch<React.SetStateAction<WorkflowEditorBinding | null>>;
};

const WorkflowEditorBridgeContext = createContext<WorkflowEditorBridgeValue | null>(null);

export const WorkflowEditorBridgeProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
   const [binding, setBinding] = useState<WorkflowEditorBinding | null>(null);
   const value = useMemo(() => ({binding, setBinding}), [binding]);
   return <WorkflowEditorBridgeContext.Provider value={value}>{children}</WorkflowEditorBridgeContext.Provider>;
};

/** The workflow open in the builder, when the dock sits beside one. */
export const useWorkflowEditorBinding = (): WorkflowEditorBinding | null =>
   useContext(WorkflowEditorBridgeContext)?.binding ?? null;

/** Registers the builder's binding while it is mounted; pass a memoized value. */
export const useRegisterWorkflowEditor = (binding: WorkflowEditorBinding | null) => {
   const setBinding = useContext(WorkflowEditorBridgeContext)?.setBinding;
   useEffect(() => {
      if (!setBinding || !binding) return;
      setBinding(binding);
      return () => setBinding((current) => (current === binding ? null : current));
   }, [binding, setBinding]);
};
